'use client'
import * as React from 'react'
import Link, { LinkProps } from 'next/link'

import { clsx } from 'clsx'

import type { VariantProps } from 'class-variance-authority'
import { useRouter } from 'next/navigation'
import { commandKeyPressed } from '~/lib/command-key-pressed'
import { $button } from './styles'

type KeysToNever<T> = {
  [K in keyof T]?: never
}

type VariantPropsOrUnstyled<VP = VariantProps<typeof $button>> =
  | (VP & {
      unstyled?: never
      focusStyled?: never
      icon?: React.ReactNode
      iconSide?: 'left' | 'right'
    })
  | ({
      unstyled: true
      focusStyled?: boolean
      icon?: React.ReactNode
      iconSide?: 'left' | 'right'
    } & Omit<KeysToNever<VP>, 'iconSide'>)

export type ButtonProps = React.JSX.IntrinsicElements['button'] &
  VariantPropsOrUnstyled & {
    takeActionOnMouseDown?: boolean
  }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      intent,
      size,
      unstyled,
      focusStyled,
      icon,
      iconPaddingLeft,
      iconPaddingRight,
      pointer,
      radius,
      align,
      iconSide = 'left',
      onClick,
      onMouseDown,
      takeActionOnMouseDown,
      ...rest
    },
    ref
  ) => {
    const handleMouseDown = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (takeActionOnMouseDown && onClick) {
          onClick(e)
        }
        onMouseDown?.(e)
      },
      [takeActionOnMouseDown, onClick, onMouseDown]
    )

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!takeActionOnMouseDown && onClick) {
          onClick(e)
        }
      },
      [takeActionOnMouseDown, onClick]
    )

    return unstyled ? (
      <button
        {...rest}
        ref={ref}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        className={clsx(className, {
          'rounded outline-1 outline-offset-2 outline-dark-control-base focus-visible:outline':
            focusStyled,
          'flex-row-reverse': iconSide === 'right',
        })}
      >
        {icon && <span className={clsx('shrink-0 icon')}>{icon}</span>}
        {children}
      </button>
    ) : (
      <button
        className={$button({
          class: className,
          intent,
          size,
          radius,
          align,
          pointer,
          iconSide,
          iconPaddingLeft:
            iconPaddingLeft || (Boolean(icon) && iconSide === 'left'),
          iconPaddingRight:
            iconPaddingRight || (Boolean(icon) && iconSide === 'right'),
        })}
        {...rest}
        ref={ref}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      >
        {icon && <span className={clsx('shrink-0 icon')}>{icon}</span>}
        {children}
      </button>
    )
  }
)

export type ButtonLinkProps = LinkProps &
  Omit<React.JSX.IntrinsicElements['a'], 'href'> &
  VariantPropsOrUnstyled & {
    takeActionOnMouseDown?: boolean
  }

export const ButtonLink = React.forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  (
    {
      children,
      className,
      intent,
      size,
      focusStyled = true,
      unstyled,
      icon,
      iconPaddingLeft,
      iconPaddingRight,
      pointer,
      iconSide = 'left',
      onMouseDown,
      takeActionOnMouseDown,
      href,
      ...rest
    },
    ref
  ) => {
    const router = useRouter()

    const handleMouseDown = React.useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (takeActionOnMouseDown && href && !commandKeyPressed(e)) {
          router.push(href.toString())
          e.preventDefault()
          e.stopPropagation()
          return
        }
        onMouseDown?.(e)
      },
      [href, onMouseDown, router, takeActionOnMouseDown]
    )

    return unstyled ? (
      <Link
        {...rest}
        href={href}
        ref={ref}
        onMouseDown={handleMouseDown}
        className={clsx(className, {
          'rounded outline-1 outline-offset-2 outline-dark-control-base focus-visible:outline':
            focusStyled,
          iconPaddingLeft:
            iconPaddingLeft || (Boolean(icon) && iconSide === 'left'),
          iconPaddingRight:
            iconPaddingRight || (Boolean(icon) && iconSide === 'right'),
          iconSide: iconSide === 'right' ? 'flex-row-reverse' : '',
        })}
      >
        {icon && <span className={clsx('shrink-0 icon')}>{icon}</span>}
        {children}
      </Link>
    ) : (
      <Link
        className={$button({
          class: className,
          intent,
          size,
          pointer,
          iconPaddingLeft:
            iconPaddingLeft || (Boolean(icon) && iconSide === 'left'),
          iconPaddingRight:
            iconPaddingRight || (Boolean(icon) && iconSide === 'right'),
          iconSide,
        })}
        {...rest}
        href={href}
        ref={ref}
        onMouseDown={handleMouseDown}
      >
        {icon && <span className={clsx('shrink-0 icon')}>{icon}</span>}
        {children}
      </Link>
    )
  }
)

Button.displayName = 'Button'

export function ButtonIsLink(
  props: Omit<ButtonLinkProps | ButtonProps, 'unstyled'>
): props is ButtonLinkProps {
  return 'href' in props
}

export const ButtonCounter = ({
  children,
  intent = 'primary',
}: {
  children: React.ReactNode
  intent?: 'primary' | 'secondary'
}) => {
  return (
    <span
      className={clsx(
        'h-[18px] w-[18px] ml-1 flex items-center justify-center border text-xs rounded-sm',
        intent === 'primary'
          ? 'bg-dark-bg bg-opacity-15 border-opacity-10 text-dark-bg border-dark-bg'
          : 'bg-dark-shade-hover border-dark-border-solid text-light-base'
      )}
    >
      {children}
    </span>
  )
}
